"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useCallback } from "react";
import { TimeWeightedVotingConfig, TimeUnit } from "./types";
import { convertToMinutes, convertFromMinutes, ensureValueInBounds } from "./utils";
import { TimeWeightedToggle, AveragingIntervalInput } from "./components";

/**
 * Props for the TimeWeightedVoting component
 */
interface TimeWeightedVotingProps {
  /** Current configuration for time-weighted voting */
  readonly config: TimeWeightedVotingConfig;
  /** Handler called when configuration changes */
  readonly onChange: (config: TimeWeightedVotingConfig) => void;
}

/**
 * TimeWeightedVoting Component
 * Main component that manages the state and orchestrates child components
 * Allows configuration of time-weighted voting settings
 */
export default function TimeWeightedVoting(t0) {
  const $ = _c(37);
  const {
    config,
    onChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [validationErrors, setValidationErrors] = useState(t1);
  let t2;
  if ($[1] !== config.averagingInterval) {
    t2 = config.averagingInterval.toString();
    $[1] = config.averagingInterval;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [inputValue, setInputValue] = useState(t2);
  let t3;
  let t4;
  if ($[3] !== config.averagingInterval) {
    t3 = () => {
      setInputValue(config.averagingInterval.toString());
    };
    t4 = [config.averagingInterval];
    $[3] = config.averagingInterval;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== config.averagingInterval || $[7] !== config.averagingIntervalUnit || $[8] !== config.enabled) {
    t5 = () => {
      if (!config.enabled) {
        setValidationErrors({});
        return;
      }
      const errors = {};
      const valueInMinutes = convertToMinutes(config.averagingInterval, config.averagingIntervalUnit);
      if (valueInMinutes < 5) {
        errors.interval = "Must be at least 5 minutes";
      }
      if (valueInMinutes > 1440) {
        errors.interval = "Must not exceed 24 hours";
      }
      setValidationErrors(errors);
    };
    $[6] = config.averagingInterval;
    $[7] = config.averagingIntervalUnit;
    $[8] = config.enabled;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== config) {
    t6 = [config];
    $[10] = config;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  if ($[12] !== config || $[13] !== onChange) {
    t7 = () => {
      onChange({
        ...config,
        enabled: !config.enabled
      });
    };
    $[12] = config;
    $[13] = onChange;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleToggle = t7;
  let t8;
  if ($[15] !== config || $[16] !== onChange) {
    t8 = value => {
      setInputValue(value);
      if (value === "") {
        return;
      }
      const numValue = parseInt(value, 10);
      if (isNaN(numValue)) {
        return;
      }
      const maxValueForUnit = config.averagingIntervalUnit === "minutes" ? 1440 : 24;
      if (numValue <= maxValueForUnit) {
        onChange({
          ...config,
          averagingInterval: numValue
        });
      } else {
        const cappedValue = maxValueForUnit;
        setInputValue(cappedValue.toString());
        onChange({
          ...config,
          averagingInterval: cappedValue
        });
      }
    };
    $[15] = config;
    $[16] = onChange;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleIntervalChange = t8;
  let t9;
  if ($[18] !== config || $[19] !== onChange) {
    t9 = unit => {
      if (unit === config.averagingIntervalUnit) {
        return;
      }
      const valueInMinutes_0 = convertToMinutes(config.averagingInterval, config.averagingIntervalUnit);
      const newInterval = ensureValueInBounds(convertFromMinutes(valueInMinutes_0, unit), unit);
      setInputValue(newInterval.toString());
      onChange({
        ...config,
        averagingInterval: newInterval,
        averagingIntervalUnit: unit
      });
    };
    $[18] = config;
    $[19] = onChange;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const handleUnitChange = t9;
  let t10;
  if ($[21] !== validationErrors) {
    t10 = Object.keys(validationErrors);
    $[21] = validationErrors;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const hasErrors = t10.length > 0;
  let t11;
  if ($[23] !== config.enabled || $[24] !== handleToggle) {
    t11 = <TimeWeightedToggle enabled={config.enabled} onToggle={handleToggle} />;
    $[23] = config.enabled;
    $[24] = handleToggle;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== config.averagingIntervalUnit || $[27] !== config.enabled || $[28] !== handleIntervalChange || $[29] !== handleUnitChange || $[30] !== hasErrors || $[31] !== inputValue || $[32] !== validationErrors) {
    t12 = config.enabled && <>{hasErrors && validationErrors.general && <div className="tw-mb-4 tw-text-red-400 tw-text-sm tw-font-medium" role="alert" data-testid="general-error">{validationErrors.general}</div>}<div className="tw-grid md:tw-grid-cols-2 tw-gap-6"><AveragingIntervalInput value={inputValue} unit={config.averagingIntervalUnit} onIntervalChange={handleIntervalChange} onUnitChange={handleUnitChange} validationError={validationErrors.interval} /></div></>;
    $[26] = config.averagingIntervalUnit;
    $[27] = config.enabled;
    $[28] = handleIntervalChange;
    $[29] = handleUnitChange;
    $[30] = hasErrors;
    $[31] = inputValue;
    $[32] = validationErrors;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t11 || $[35] !== t12) {
    t13 = <section className="tw-mt-6 tw-border-t tw-border-iron-700 tw-pt-6" data-testid="time-weighted-voting">{t11}{t12}</section>;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
}